import React, { useRef, useState } from 'react';
import styled, { DefaultTheme, keyframes, withTheme } from 'styled-components';
import FileCopyLineIcon from 'remixicon-react/FileCopyLineIcon';
import CheckLineIcon from 'remixicon-react/CheckLineIcon';

type Props = {
    value: string;
    tooltip?: string;
    theme: DefaultTheme;
};

const CopyableCell: React.FC<Props> = ({ value, tooltip = 'Copy to clipboard', theme }: Props) => {
    const [copied, setCopied] = useState(false);
    const [fading, setFading] = useState(false);
    const fadeTimer = useRef<ReturnType<typeof setTimeout>>();

    const handleCopy = (e: React.MouseEvent) => {
        e.stopPropagation();
        navigator.clipboard.writeText(value);
        clearTimeout(fadeTimer.current);
        setFading(false);
        setCopied(true);
    };

    const handleAnimationEnd = () => {
        setFading(true);
        fadeTimer.current = setTimeout(() => {
            setCopied(false);
            setFading(false);
        }, 200);
    };

    return (
        <CopyableCellWrapper data-testid="copyable-cell">
            <CopyableCellText>{value}</CopyableCellText>
            <CopyButtonStyled
                type="button"
                $copied={copied}
                $fading={fading}
                onClick={handleCopy}
                title={tooltip}
                data-testid="copyable-cell-button"
            >
                {copied ? (
                    <CheckLineIcon size={theme.icon.size.small} />
                ) : (
                    <FileCopyLineIcon size={theme.icon.size.small} />
                )}
            </CopyButtonStyled>
            {copied && (
                <CopiedTooltip
                    onAnimationEnd={handleAnimationEnd}
                    data-testid="copyable-cell-tooltip"
                >
                    Copied!
                </CopiedTooltip>
            )}
        </CopyableCellWrapper>
    );
};

const fadeInOut = keyframes`
    0% { opacity: 0; transform: translateY(3px); }
    10% { opacity: 1; transform: translateY(0); }
    75% { opacity: 1; transform: translateY(0); }
    100% { opacity: 0; transform: translateY(-3px); }
`;

const CopyableCellWrapper = styled.span`
    position: relative;
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
`;

const CopyableCellText = styled.span`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const CopyButtonStyled = styled.button<{ $copied: boolean; $fading: boolean }>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    border: none;
    background: none;
    padding: 0.3rem;
    border-radius: 0.4rem;
    cursor: pointer;
    color: ${props => props.theme.colors.text.secondary};
    opacity: ${props => (props.$copied && !props.$fading ? 1 : 0)};
    transition: opacity 200ms ease-in, color 150ms ease, background 150ms ease;

    ${CopyableCellWrapper}:hover & {
        opacity: 1;
    }

    &:hover {
        background: ${props => props.theme.colors.button.light};
    }
`;

const CopiedTooltip = styled.span`
    position: absolute;
    bottom: calc(100% + 0.6rem);
    left: 50%;
    transform: translateX(-50%);
    background-color: ${props => props.theme.colors.accent};
    color: ${props => props.theme.colors.text.primary};
    font-size: ${props => props.theme.text.size.small};
    font-weight: 500;
    padding: 0.3rem 0.7rem;
    border-radius: 0.4rem;
    white-space: nowrap;
    pointer-events: none;
    box-shadow: 0 0.2rem 0.5rem rgba(0, 0, 0, 0.15);
    animation: ${fadeInOut} 1s ease forwards;

    &::after {
        content: '';
        position: absolute;
        top: 100%;
        left: 50%;
        margin-left: -0.4rem;
        border: 0.4rem solid transparent;
        border-top-color: ${props => props.theme.colors.accent};
    }
`;

export default withTheme(CopyableCell) as React.ComponentType<Omit<Props, 'theme'>>;
